/**
 * @ Mention Autocomplete Component
 * 
 * Shows autocomplete suggestions when user types @ in chat input.
 * Supports block references like @14, @14-17, @OUTLINE:14
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Input, Button } from '@/components/ui';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';

// Inline SVG icons to avoid external dependency
const HashIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
  </svg>
);

const FileTextIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ChevronRightIcon available for future hierarchical display
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-3 h-3"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// Search icon for the search field
const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/**
 * Block suggestion for autocomplete
 */
interface BlockSuggestion {
  id: string;
  type: 'block' | 'document' | 'special' | 'recent';
  displayText: string;
  insertText: string;
  description?: string;
  icon?: React.ReactNode;
  depth?: number;
}

interface AtMentionAutocompleteProps {
  isOpen: boolean;
  query: string;  // Text after @ symbol
  onSelect: (insertText: string) => void;
  onClose: () => void;
  
  // Data for suggestions
  currentDocumentBlocks?: Array<{ id: string; preview: string; type: string; depth?: number }>;
  projectDocuments?: Array<{ id: string; name: string; alias: string }>;
  recentReferences?: string[];
}

/**
 * Parse the @ query to determine what type of suggestions to show
 */
function parseAtQuery(query: string): {
  type: 'block' | 'document' | 'special' | 'all';
  documentAlias?: string;
  blockSearch?: string;
} {
  // @@special
  if (query.startsWith('@')) {
    return { type: 'special', blockSearch: query.slice(1) };
  }
  
  // @DOC:number - cross-document reference
  const crossDocMatch = query.match(/^([A-Z][A-Z0-9_-]*):(.*)$/i);
  if (crossDocMatch) {
    return { 
      type: 'block', 
      documentAlias: crossDocMatch[1].toUpperCase(),
      blockSearch: crossDocMatch[2],
    };
  }
  
  // @number - block reference
  if (/^\d+/.test(query)) {
    return { type: 'block', blockSearch: query };
  }
  
  // @text - could be document or block
  return { type: 'all', blockSearch: query };
}

/**
 * @ Mention Autocomplete Dropdown
 */
export function AtMentionAutocomplete({
  isOpen,
  query,
  onSelect,
  onClose,
  currentDocumentBlocks = [],
  projectDocuments = [],
  recentReferences = [],
}: AtMentionAutocompleteProps) {
  const [localSearch, setLocalSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Combine parent query with local search
  const effectiveQuery = localSearch || query;

  // Parse query to determine suggestion type
  const parsed = useMemo(() => parseAtQuery(effectiveQuery), [effectiveQuery]);

  // Build suggestions
  const suggestions = useMemo<BlockSuggestion[]>(() => {
    const results: BlockSuggestion[] = [];

    // Special references (@@last, etc.)
    if (parsed.type === 'special' || parsed.type === 'all') {
      const specialRefs = [
        { id: 'last', name: 'last', description: 'Last edited block' },
      ];

      for (const ref of specialRefs) {
        if (!parsed.blockSearch || ref.name.includes(parsed.blockSearch.toLowerCase())) {
          results.push({
            id: `special-${ref.id}`,
            type: 'special',
            displayText: `@@${ref.name}`,
            insertText: `@@${ref.name}`,
            description: ref.description,
            icon: <ClockIcon className="w-4 h-4 text-warning" />,
          });
        }
      }
    }

    // Recent references
    if (parsed.type === 'all' && recentReferences.length > 0) {
      for (const ref of recentReferences.slice(0, 3)) {
        if (!query || ref.toLowerCase().includes(query.toLowerCase())) {
          results.push({
            id: `recent-${ref}`,
            type: 'recent',
            displayText: ref,
            insertText: ref,
            description: 'Recently used',
            icon: <ClockIcon className="w-4 h-4 text-muted-foreground" />,
          });
        }
      }
    }

    // Document references
    if (parsed.type === 'all' || parsed.type === 'document') {
      for (const doc of projectDocuments) {
        if (!parsed.blockSearch || 
            doc.name.toLowerCase().includes(parsed.blockSearch.toLowerCase()) ||
            doc.alias.toLowerCase().includes(parsed.blockSearch.toLowerCase())) {
          results.push({
            id: `doc-${doc.id}`,
            type: 'document',
            displayText: `@${doc.alias}:`,
            insertText: `@${doc.alias}:`,
            description: doc.name,
            icon: <FileTextIcon className="w-4 h-4 text-link" />,
          });
        }
      }
    }

    // Block references - always show all blocks in document order
    // Filter by search text if provided
    const searchText = parsed.blockSearch?.toLowerCase() || '';
    
    for (const block of currentDocumentBlocks) {
      // Filter by ID or preview text if searching
      if (searchText && 
          !block.id.toLowerCase().includes(searchText) && 
          !block.preview.toLowerCase().includes(searchText)) {
        continue;
      }

      // If searching cross-doc, prefix with document alias
      const prefix = parsed.documentAlias ? `@${parsed.documentAlias}:` : '@';
      
      // Use different icon for sections vs blocks
      const isSection = block.type === 'section';
      
      results.push({
        id: `block-${block.id}`,
        type: 'block',
        displayText: `${prefix}${block.id}`,
        insertText: `${prefix}${block.id}`,
        description: block.preview.slice(0, 60) + (block.preview.length > 60 ? '...' : ''),
        icon: isSection 
          ? <FileTextIcon className="w-4 h-4 text-link" />
          : <HashIcon className="w-4 h-4 text-success" />,
        depth: block.depth || 0,
      });
    }

    return results; // Show all blocks in document order
  }, [query, parsed, currentDocumentBlocks, projectDocuments, recentReferences]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleKeySelect = useCallback((index: number) => {
    if (suggestions[index]) {
      onSelect(suggestions[index].insertText);
    }
  }, [suggestions, onSelect]);

  const { selectedIndex, setSelectedIndex } = useAutocompleteKeyboard({
    itemCount: suggestions.length,
    onSelect: handleKeySelect,
    onClose,
    enabled: isOpen,
    wrapAround: false,
    selectKeys: ['Enter', 'Tab'],
    listenerTarget: 'document',
    resetDeps: [effectiveQuery],
  });

  // Show even if no suggestions yet (to allow searching)
  if (!isOpen) return null;

  return (
    <div
      className="command-autocomplete"
    >
      {/* Header with search */}
      <div className="px-3 py-2 bg-success-light border-b border-success">
        <div className="flex items-center gap-2 text-xs font-semibold text-success mb-2">
          <HashIcon className="w-3 h-3" />
          <span>Block References</span>
          <span className="text-success">({currentDocumentBlocks.length} blocks)</span>
        </div>
        <div className="relative">
          <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground z-10" />
          <Input
            ref={searchInputRef}
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onKeyDown={(e) => {
              // Don't let these keys bubble up to the document handler
              if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
                e.stopPropagation();
              }
            }}
            placeholder="Search blocks by ID or content..."
            className="w-full pl-7 pr-2 py-1.5 text-sm focus:ring-success focus:border-transparent"
          />
        </div>
      </div>

      {/* Suggestions list */}
      <div ref={scrollContainerRef} className="max-h-80 overflow-y-auto py-1">
        {suggestions.length === 0 ? (
          <div className="px-4 py-6 text-center text-muted-foreground">
            <div className="text-2xl mb-2">📄</div>
            <div className="text-sm">
              {currentDocumentBlocks.length === 0 
                ? 'No document open' 
                : `No blocks matching "${effectiveQuery}"`}
            </div>
          </div>
        ) : (
          suggestions.map((suggestion, index) => {
            const indentPx = (suggestion.depth || 0) * 16;
            const isSection = suggestion.type === 'block' && suggestion.displayText.includes('sec-');
            
            return (
              <Button
                key={suggestion.id}
                data-block-item
                data-index={index}
                variant="ghost"
                className={`w-full flex items-start gap-2 h-auto py-1.5 text-left justify-start rounded-none ${
                  index === selectedIndex
                    ? 'bg-info-light'
                    : 'hover:bg-muted'
                } ${isSection ? 'bg-muted/50' : ''}`}
                onClick={() => onSelect(suggestion.insertText)}
                onMouseEnter={() => setSelectedIndex(index)}
                // eslint-disable-next-line no-restricted-syntax -- Dynamic indent based on block depth
                style={{ paddingLeft: `${12 + indentPx}px` }}
              >
                <span className="flex-shrink-0 mt-0.5">
                  {suggestion.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-info">
                      {suggestion.displayText}
                    </span>
                    {isSection && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-info-light text-info">
                        section
                      </span>
                    )}
                  </div>
                  {suggestion.description && (
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {suggestion.description}
                    </div>
                  )}
                </div>
              </Button>
            );
          })
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-1.5 bg-muted border-t border-border">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">↵</kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}

export default AtMentionAutocomplete;

